import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, TrendingUp, Sparkles, CheckCircle2, RefreshCw, X, Copy } from "lucide-react";

export default function CostAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeAck, setIncludeAck] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [draftModal, setDraftModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/alerts/cost-creep", {
        params: { include_acknowledged: includeAck },
      });
      setAlerts(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [includeAck]);

  const acknowledge = async (a) => {
    try {
      await api.post(`/alerts/${a.id}/acknowledge`);
      toast.success("Marked reviewed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const draftEmail = async (a) => {
    setDrafting(a.id);
    try {
      const r = await api.post("/ai/renegotiation-email", { alert_id: a.id });
      setDraftModal({ alert: a, text: r.data.email_draft });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setDrafting(null);
  };

  return (
    <div className="space-y-6" data-testid="alerts-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-red-400 font-mono-plex mb-2 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Margin Protection
          </div>
          <h1 className="font-display text-4xl font-bold">Cost Alerts</h1>
          <p className="text-slate-500 text-sm mt-1">
            Flagged when a supplier's unit price rises &gt;5% between deliveries.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={includeAck}
            onChange={(e) => setIncludeAck(e.target.checked)}
            data-testid="toggle-ack"
            className="accent-amber-500"
          />
          Show reviewed
        </label>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : alerts.length === 0 ? (
        <div className="surface-card rounded-xl p-12 text-center text-slate-500">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No price hikes detected</div>
          <p className="text-sm">You'll see alerts here when a delivered PO's unit price is more than 5% above the last recorded cost.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.id} className={`surface-card rounded-xl p-5 ${a.acknowledged ? "opacity-60" : ""}`} data-testid={`alert-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-xl font-bold">{a.item_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 font-mono-plex">
                      +{a.pct_change}%
                    </span>
                    {a.acknowledged && (
                      <span className="text-xs text-slate-500 font-mono-plex">reviewed</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-400 mb-2">
                    Supplier: <span className="text-slate-200">{a.supplier_name}</span> · PO{" "}
                    <span className="font-mono-plex text-amber-400">{a.po_number}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-mono-plex text-slate-500 line-through">
                      ${a.previous_cost.toFixed(2)}
                    </span>
                    <TrendingUp className="w-4 h-4 text-red-400" />
                    <span className="font-mono-plex text-red-400 font-semibold">
                      ${a.new_cost.toFixed(2)}
                    </span>
                    <span className="text-xs text-slate-500 font-mono-plex">
                      · {new Date(a.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!a.acknowledged && (
                    <>
                      <button
                        onClick={() => draftEmail(a)}
                        disabled={drafting === a.id}
                        data-testid={`draft-email-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1 disabled:opacity-60"
                      >
                        {drafting === a.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        AI renegotiate
                      </button>
                      <button
                        onClick={() => acknowledge(a)}
                        data-testid={`ack-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded border border-white/10 text-slate-300 hover:bg-white/[0.03] flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Mark reviewed
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {draftModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-xl bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4" data-testid="draft-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-2xl font-bold">Renegotiation draft</div>
                <div className="text-xs text-slate-500 font-mono-plex mt-1">
                  To: {draftModal.alert.supplier_name}
                </div>
              </div>
              <button onClick={() => setDraftModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <textarea
              rows="12"
              value={draftModal.text}
              onChange={(e) => setDraftModal({ ...draftModal, text: e.target.value })}
              className="w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500 font-mono-plex"
              data-testid="draft-textarea"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(draftModal.text);
                  toast.success("Copied to clipboard");
                }}
                className="px-4 py-2 rounded-md border border-white/10 text-sm flex items-center gap-2"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
              <button
                onClick={() => setDraftModal(null)}
                className="btn-amber rounded-md px-4 py-2 text-sm"
              >
                Done
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Tip: paste this into your supplier email thread. You can send POs directly from the app, but negotiation emails often work better from your own inbox.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
